// src/services/pagesServices.js
import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api/pages`;

// Lit le token à chaque appel (utile si l’utilisateur se reconnecte)
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* =========================
   Mini cache mémoire (5 min)
   ========================= */
const cache = new Map();
const TTL = 5 * 60 * 1000;
const makeKey = (name, params) => `${name}:${JSON.stringify(params || {})}`;
const getCache = (key) => {
  const it = cache.get(key);
  if (!it) return null;
  if (Date.now() - it.t > TTL) {
    cache.delete(key);
    return null;
  }
  return it.v;
};
const setCache = (key, v) => cache.set(key, { v, t: Date.now() });

/* =========================
   Helpers
   ========================= */
const buildQuery = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (typeof v === "boolean") usp.append(k, v ? "true" : "false");
    else usp.append(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

/* =========================
   Public
   ========================= */

export const getAllPages = async () => {
  const key = makeKey("getAllPages");
  const hit = getCache(key);
  if (hit) return hit;
  try {
    const { data } = await axios.get(`${baseUrl}/getAllPages`, {
      headers: authHeaders(),
    });
    const out = Array.isArray(data) ? data : [];
    setCache(key, out);
    return out;
  } catch (e) {
    console.error("Erreur getAllPages:", e);
    return [];
  }
};

export const getPageBySlug = async (slug, opts = {}) => {
  const { locale = "fr", include_inactive = false, bypassCache = false } = opts;
  const key = makeKey("getPageBySlug", { slug, locale, include_inactive });
  if (!bypassCache) {
    const hit = getCache(key);
    if (hit) return hit;
  }
  try {
    const url =
      `${baseUrl}/getBySlug/${encodeURIComponent(slug)}` +
      buildQuery({ locale, include_inactive });
    const { data } = await axios.get(url, { headers: authHeaders() });
    setCache(key, data || null);
    return data || null;
  } catch (e) {
    console.error("Erreur getPageBySlug:", e);
    return null;
  }
};

export const getChildrenBySlug = async (slug, opts = {}) => {
  const { locale = "fr", include_inactive = false } = opts;
  const key = makeKey("getChildrenBySlug", { slug, locale, include_inactive });
  const hit = getCache(key);
  if (hit) return hit;
  try {
    const url =
      `${baseUrl}/getChildrenBySlug/${encodeURIComponent(slug)}` +
      buildQuery({ locale, include_inactive });
    const { data } = await axios.get(url, { headers: authHeaders() });
    const out = Array.isArray(data) ? data : [];
    setCache(key, out);
    return out;
  } catch (e) {
    console.error("Erreur getChildrenBySlug:", e);
    return [];
  }
};

export const getBreadcrumbs = async (slug, opts = {}) => {
  const { locale = "fr" } = opts;
  const key = makeKey("getBreadcrumbs", { slug, locale });
  const hit = getCache(key);
  if (hit) return hit;
  try {
    const url =
      `${baseUrl}/getBreadcrumbs/${encodeURIComponent(slug)}` +
      buildQuery({ locale });
    const { data } = await axios.get(url, { headers: authHeaders() });
    const out = Array.isArray(data) ? data : [];
    setCache(key, out);
    return out;
  } catch (e) {
    console.error("Erreur getBreadcrumbs:", e);
    return [];
  }
};

// Utils facultatifs
export const toPageHref = (slug, locale = "fr") =>
  locale === "fr" ? `/pages/${slug}` : `/${locale}/pages/${slug}`;

export const pagesCache = {
  clear: () => cache.clear(),
};

/* =========================
   Admin / CMS
   ========================= */

// Recherche / pagination admin
// params: { q, locale, parent_id, is_active, page=1, limit=24, sort='created_at'|'title', dir='DESC'|'ASC' }
export const searchPages = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/search${qs}`, {
      headers: authHeaders(),
    });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number.isFinite(data?.total) ? data.total : 0,
      page: Number.isFinite(data?.page) ? data.page : 1,
      limit: Number.isFinite(data?.limit) ? data.limit : 24,
      totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    };
  } catch (e) {
    console.error("Erreur searchPages:", e);
    return { items: [], total: 0, page: 1, limit: 24, totalPages: 1 };
  }
};

export const getPageById = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (e) {
    console.error("Erreur getPageById:", e);
    return null;
  }
};

// payload: { locale, title*, subtitle?, slug?, content_html?, excerpt?, cover_image_url?, parent_id?, template?, redirect_url?, seo_title?, seo_description?, seo_keywords?, is_active?, published_at? }
export const createPage = async (payload) => {
  try {
    const { data } = await axios.post(`${baseUrl}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    pagesCache.clear();
    return data || null;
  } catch (e) {
    console.error("Erreur createPage:", e);
    return null;
  }
};

export const updatePage = async (id, payload) => {
  if (!id) return null;
  try {
    const { data } = await axios.put(`${baseUrl}/${id}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    pagesCache.clear();
    return data || null;
  } catch (e) {
    console.error("Erreur updatePage:", e);
    return null;
  }
};

export const patchPage = async (id, partial) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}`, partial, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    pagesCache.clear();
    return data || null;
  } catch (e) {
    console.error("Erreur patchPage:", e);
    return null;
  }
};

export const togglePageActive = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}/toggle`, null, {
      headers: authHeaders(),
    });
    pagesCache.clear();
    return data || null;
  } catch (e) {
    console.error("Erreur togglePageActive:", e);
    return null;
  }
};

export const deletePage = async (id) => {
  if (!id) return false;
  try {
    await axios.delete(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    pagesCache.clear();
    return true;
  } catch (e) {
    console.error("Erreur deletePage:", e);
    return false;
  }
};
